import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldCheck, Eye, EyeSlash, Lock, Envelope, Warning, ArrowLeft, PaperPlaneTilt, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { checkAuth, user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'reset'
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetMsg, setResetMsg] = useState('');

  // If already logged in as admin, redirect
  useEffect(() => {
    if (user && user.role === 'admin') {
      navigate('/admin', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please enter email and password'); return; }
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (!result.success) {
        setError(result.error || 'Invalid credentials');
        setLoading(false);
        return;
      }
      if (result.user?.role !== 'admin') {
        setError('Access denied. Admin credentials required.');
        await axios.post(`${API_URL}/api/auth/logout`, {}).catch(() => {});
        setLoading(false);
        return;
      }
      toast.success('Welcome back, Admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) { setError('Please enter your admin email'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/forgot-password`, { email: resetEmail });
      setResetMsg(res.data.message || 'If this email exists, a reset link has been sent.');
      setView('reset');
      toast.success('Reset instructions sent');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetToken || !newPassword) { setError('Please enter the reset token and new password'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token: resetToken, new_password: newPassword });
      toast.success('Password reset successfully!');
      setView('login');
      setResetEmail('');
      setResetToken('');
      setNewPassword('');
      setResetMsg('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired reset token');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center px-4">
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-600/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600/5 rounded-full blur-3xl" />
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(99,102,241,0.05) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-600/20 border border-indigo-500/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldCheck size={32} className="text-indigo-400" weight="fill" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight" data-testid="admin-login-title">
            L4S Admin
          </h1>
          <p className="text-[#6b7a99] text-sm mt-1">Management Portal</p>
        </div>

        {/* Login Card */}
        <div className="bg-[#111827]/80 backdrop-blur-xl border border-[#1e293b] rounded-2xl p-6 shadow-2xl shadow-black/20" data-testid="admin-login-card">

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm" data-testid="admin-login-error">
              <Warning size={16} weight="bold" />
              {error}
            </div>
          )}

          {/* LOGIN VIEW */}
          {view === 'login' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-[#94a3b8] text-xs font-medium mb-1.5 block">Email</Label>
                <div className="relative">
                  <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a5578]" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@spacebooking.com"
                    className="bg-[#0f1629] border-[#1e293b] text-white h-11 pl-10 rounded-xl text-sm placeholder:text-[#334155] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                    data-testid="admin-email-input" autoComplete="email" />
                </div>
              </div>
              <div>
                <Label className="text-[#94a3b8] text-xs font-medium mb-1.5 block">Password</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a5578]" />
                  <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="bg-[#0f1629] border-[#1e293b] text-white h-11 pl-10 pr-10 rounded-xl text-sm placeholder:text-[#334155] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                    data-testid="admin-password-input" autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4a5578] hover:text-[#94a3b8] transition-colors"
                    data-testid="toggle-password-visibility">
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={() => { setView('forgot'); setError(''); }}
                  className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                  data-testid="forgot-password-link">
                  Forgot password?
                </button>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition-all disabled:opacity-50"
                data-testid="admin-login-submit">
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Authenticating...
                  </div>
                ) : (
                  <><ShieldCheck size={16} className="mr-2" weight="bold" /> Sign In to Admin Portal</>
                )}
              </Button>
            </form>
          )}

          {/* FORGOT PASSWORD VIEW */}
          {view === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <button type="button" onClick={() => { setView('login'); setError(''); }}
                className="flex items-center gap-1.5 text-xs text-[#6b7a99] hover:text-indigo-400 transition-colors mb-2"
                data-testid="back-to-login">
                <ArrowLeft size={14} /> Back to login
              </button>
              <div>
                <h3 className="text-white font-semibold text-sm mb-1">Forgot Password</h3>
                <p className="text-[#6b7a99] text-xs">Enter your admin email and we'll send you a reset token.</p>
              </div>
              <div>
                <Label className="text-[#94a3b8] text-xs font-medium mb-1.5 block">Admin Email</Label>
                <div className="relative">
                  <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a5578]" />
                  <Input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="admin@spacebooking.com"
                    className="bg-[#0f1629] border-[#1e293b] text-white h-11 pl-10 rounded-xl text-sm placeholder:text-[#334155] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                    data-testid="forgot-email-input" autoComplete="email" />
                </div>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition-all disabled:opacity-50"
                data-testid="forgot-submit">
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </div>
                ) : (
                  <><PaperPlaneTilt size={16} className="mr-2" weight="bold" /> Send Reset Token</>
                )}
              </Button>
            </form>
          )}

          {/* RESET PASSWORD VIEW */}
          {view === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <button type="button" onClick={() => { setView('forgot'); setError(''); }}
                className="flex items-center gap-1.5 text-xs text-[#6b7a99] hover:text-indigo-400 transition-colors mb-2"
                data-testid="back-to-forgot">
                <ArrowLeft size={14} /> Back
              </button>
              {resetMsg && (
                <div className="flex items-start gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400 text-xs" data-testid="reset-msg">
                  <CheckCircle size={16} weight="bold" className="shrink-0 mt-0.5" />
                  <div>
                    <p>{resetMsg}</p>
                    <p className="text-[#6b7a99] mt-1">Check server logs for the reset token (emails are currently mocked).</p>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-[#94a3b8] text-xs font-medium mb-1.5 block">Reset Token</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a5578]" />
                  <Input type="text" value={resetToken} onChange={(e) => setResetToken(e.target.value)}
                    placeholder="Paste reset token from email"
                    className="bg-[#0f1629] border-[#1e293b] text-white h-11 pl-10 rounded-xl text-sm placeholder:text-[#334155] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                    data-testid="reset-token-input" />
                </div>
              </div>
              <div>
                <Label className="text-[#94a3b8] text-xs font-medium mb-1.5 block">New Password</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a5578]" />
                  <Input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 chars)"
                    className="bg-[#0f1629] border-[#1e293b] text-white h-11 pl-10 pr-10 rounded-xl text-sm placeholder:text-[#334155] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20"
                    data-testid="new-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4a5578] hover:text-[#94a3b8] transition-colors">
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-all disabled:opacity-50"
                data-testid="reset-submit">
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Resetting...
                  </div>
                ) : (
                  <><Lock size={16} className="mr-2" weight="bold" /> Reset Password</>
                )}
              </Button>
            </form>
          )}

          <div className="mt-5 pt-4 border-t border-[#1e293b] text-center">
            <button onClick={() => navigate('/buyer')}
              className="text-xs text-[#4a5578] hover:text-indigo-400 transition-colors"
              data-testid="back-to-site-link">
              Back to Look4Space
            </button>
          </div>
        </div>

        {/* Security Note */}
        <p className="text-center text-[10px] text-[#334155] mt-6">
          Secured access. Unauthorized attempts are logged.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
